import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { getApiUrl, apiFetch } from '../../config/api';
import EntityLink from '../../components/Common/EntityLink';
import { formatAppDate } from '../../utils/dateUtils';
import './HomePage.css';

// #279: material-return lines whose inspection accepted + rejected less than was declared.
// Reported for management to analyse; nobody is charged for them.
const ReturnShortagesCard = ({ limit = 20 }) => {
  const { t } = useTranslation('common');
  const location = useLocation();
  const dashboardPath = `${location.pathname}${location.search}`;
  const [data, setData] = useState(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    let cancelled = false;
    apiFetch(getApiUrl(`/api/dashboard/return-shortages?limit=${limit}`))
      .then(res => (res.ok ? res.json() : Promise.reject(res.status)))
      .then(json => { if (!cancelled) setData(json); })
      .catch(() => { if (!cancelled) setFailed(true); });
    return () => { cancelled = true; };
  }, [limit]);

  const qty = (value) => Number(value || 0).toLocaleString(undefined, { maximumFractionDigits: 3 });

  return (
    <div className={`dashboard-section return-shortages-card ${data?.total ? 'alert' : ''}`} data-testid="return-shortages-card">
      <div className="section-header">
        <h2 className="section-title">
          {t('returnShortages.title')}{data?.total ? ` (${data.total})` : ''}
        </h2>
      </div>
      <p className="return-shortages-hint">{t('returnShortages.hint')}</p>
      {failed ? (
        <div className="empty-state">{t('returnShortages.loadError')}</div>
      ) : !data ? (
        <div className="empty-state">{t('returnShortages.loading')}</div>
      ) : data.items.length === 0 ? (
        <div className="empty-state">{t('returnShortages.empty')}</div>
      ) : (
        <div style={{ overflowX: 'auto' }}>
          <table className="data-table">
            <thead>
              <tr>
                <th>{t('returnShortages.columns.return')}</th>
                <th>{t('returnShortages.columns.requisition')}</th>
                <th>{t('returnShortages.columns.material')}</th>
                <th style={{ textAlign: 'right' }}>{t('returnShortages.columns.declared')}</th>
                <th style={{ textAlign: 'right' }}>{t('returnShortages.columns.accepted')}</th>
                <th style={{ textAlign: 'right' }}>{t('returnShortages.columns.rejected')}</th>
                <th style={{ textAlign: 'right' }}>{t('returnShortages.columns.missing')}</th>
                <th>{t('returnShortages.columns.inspectedBy')}</th>
                <th>{t('returnShortages.columns.date')}</th>
              </tr>
            </thead>
            <tbody>
              {data.items.map(row => (
                <tr key={row.return_item_id}>
                  <td>{row.return_number}</td>
                  <td>
                    {row.requisition_number || '-'}
                    {row.po_number && (
                      <div>
                        <EntityLink type="production-order" id={row.po_number} label={row.po_number}
                          returnLabel="Dashboard" returnPath={dashboardPath} />
                      </div>
                    )}
                  </td>
                  <td>
                    <EntityLink type="inventory-item" id={row.inventory_item_id} label={row.item_code}
                      returnLabel="Dashboard" returnPath={dashboardPath} />
                    <div>{row.item_name}</div>
                  </td>
                  <td style={{ textAlign: 'right' }}>{qty(row.quantity_returned)} {row.unit_of_measure}</td>
                  <td style={{ textAlign: 'right' }}>{qty(row.quantity_accepted)}</td>
                  <td style={{ textAlign: 'right' }}>{qty(row.quantity_rejected)}</td>
                  <td style={{ textAlign: 'right', fontWeight: 600 }}>{qty(row.quantity_missing)} {row.unit_of_measure}</td>
                  <td>{row.inspected_by_name || '-'}</td>
                  <td>{formatAppDate(row.inspected_date, 'datetime')}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default ReturnShortagesCard;
